import React from 'react';

/**
 * Formatting for the text people write in the content library.
 *
 * A blog post used to be one long unstyled run of characters: whatever was
 * typed, split into paragraphs on blank lines. No headings inside an article,
 * no emphasis on a word, no links, no lists — which for a blog is most of
 * what writing is.
 *
 * The notation is the small, familiar subset of Markdown, chosen because it
 * is what people already type by habit: a line starting with `#` is a
 * heading, `-` is a bullet, `**bold**`, `*italic*`, `[words](where)`. Nothing
 * here is required — plain text with blank lines between paragraphs renders
 * exactly as it always did, which matters because every entry written so far
 * is plain text.
 *
 * Deliberately not a full Markdown parser and deliberately not HTML. This
 * text is written by brand owners and rendered on their public site; a
 * parser that accepted raw HTML would be an injection route, and one that
 * accepted tables and footnotes would be a large dependency for a feature
 * whose whole point is bolding a word.
 */

/** Inline spans: bold, italic, code, links. Returns an array of React nodes. */
const inline = (text, keyPrefix) => {
  const out = [];
  // One pass, one regex, alternating between the forms — so `**bold**` inside
  // a link and a link inside bold both resolve the same way round every time.
  const pattern = /(\*\*[^*]+\*\*)|(\*[^*]+\*)|(`[^`]+`)|(\[[^\]]+\]\([^)\s]+\))/g;
  let last = 0;
  let m = pattern.exec(text);
  let i = 0;

  while (m) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const token = m[0];
    const key = `${keyPrefix}-${i}`;
    i += 1;

    if (token.startsWith('**')) {
      out.push(<strong key={key}>{token.slice(2, -2)}</strong>);
    } else if (token.startsWith('`')) {
      out.push(
        <code
          key={key}
          style={{
            background: 'rgba(127,127,127,0.14)',
            borderRadius: 4,
            padding: '1px 5px',
            fontSize: '0.92em',
          }}
        >
          {token.slice(1, -1)}
        </code>,
      );
    } else if (token.startsWith('[')) {
      const split = token.indexOf('](');
      const label = token.slice(1, split);
      const href = token.slice(split + 2, -1);
      // Only paths and http(s). A `javascript:` link typed into a brand's
      // blog post would run on their own domain, against their own visitors.
      const safe = /^(https?:\/\/|\/(?!\/)|#|mailto:|tel:)/i.test(href) ? href : '#';
      const external = /^https?:\/\//i.test(safe);
      out.push(
        <a
          key={key}
          href={safe}
          style={{ color: 'inherit', textDecoration: 'underline' }}
          {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        >
          {label}
        </a>,
      );
    } else {
      out.push(<em key={key}>{token.slice(1, -1)}</em>);
    }

    last = m.index + token.length;
    m = pattern.exec(text);
  }

  if (last < text.length) out.push(text.slice(last));
  return out.length ? out : [text];
};

/**
 * Turn written text into block elements.
 *
 * `scale` sizes the body copy; headings step up from it, so the same article
 * reads correctly as a full detail page and as a card in a list.
 */
export const renderRichText = (raw, { scale = 17, color } = {}) => {
  // Word processors and web pages paste non-breaking spaces between words and
  // Windows line endings between lines. Left alone, the first stops text
  // wrapping where it should and the second leaves a visible stray character
  // at the end of every line.
  const text = String(raw || '')
    .replace(/\r\n?/g, '\n')
    .replace(/\u00a0/g, ' ');
  if (!text.trim()) return null;

  const blocks = [];
  const lines = text.split(/\r?\n/);
  let paragraph = [];
  let list = null;

  const flushParagraph = () => {
    if (!paragraph.length) return;
    const key = `p-${blocks.length}`;
    blocks.push(
      // `pre-wrap` so single line breaks survive.
      //
      // Text pasted from a document or a web page usually separates its
      // paragraphs with ONE newline, not a blank line. HTML collapses those,
      // so a pasted article arrived as one unbroken wall of words. Keeping
      // them means what someone pasted is what they see.
      <p
        key={key}
        style={{
          fontSize: scale, lineHeight: 1.7, margin: '0 0 16px', color,
          whiteSpace: 'pre-wrap',
        }}
      >
        {inline(paragraph.join('\n'), key)}
      </p>,
    );
    paragraph = [];
  };

  const flushList = () => {
    if (!list) return;
    const key = `l-${blocks.length}`;
    const Tag = list.ordered ? 'ol' : 'ul';
    blocks.push(
      <Tag key={key} style={{ fontSize: scale, lineHeight: 1.7, margin: '0 0 16px', paddingLeft: 22, color }}>
        {list.items.map((item, n) => (
          <li key={`${key}-${n}`} style={{ marginBottom: 6 }}>{inline(item, `${key}-${n}`)}</li>
        ))}
      </Tag>,
    );
    list = null;
  };

  const flushAll = () => { flushParagraph(); flushList(); };

  lines.forEach((line) => {
    const trimmed = line.trim();

    if (!trimmed) { flushAll(); return; }

    const heading = /^(#{1,3})\s+(.*)$/.exec(trimmed);
    if (heading) {
      flushAll();
      const level = heading[1].length;
      const Tag = `h${Math.min(level + 1, 4)}`;
      const sizes = { 1: scale * 1.8, 2: scale * 1.4, 3: scale * 1.15 };
      const key = `h-${blocks.length}`;
      blocks.push(
        <Tag
          key={key}
          style={{
            fontSize: sizes[level],
            lineHeight: 1.25,
            margin: '28px 0 12px',
            fontWeight: 700,
            color,
          }}
        >
          {inline(heading[2], key)}
        </Tag>,
      );
      return;
    }

    if (/^>\s?/.test(trimmed)) {
      flushAll();
      const key = `q-${blocks.length}`;
      blocks.push(
        <blockquote
          key={key}
          style={{
            margin: '0 0 16px',
            padding: '4px 0 4px 16px',
            borderLeft: '3px solid rgba(127,127,127,0.4)',
            fontSize: scale,
            lineHeight: 1.7,
            fontStyle: 'italic',
            color,
          }}
        >
          {inline(trimmed.replace(/^>\s?/, ''), key)}
        </blockquote>,
      );
      return;
    }

    const bullet = /^[-*]\s+(.*)$/.exec(trimmed);
    const numbered = /^\d+[.)]\s+(.*)$/.exec(trimmed);
    if (bullet || numbered) {
      flushParagraph();
      const ordered = Boolean(numbered);
      // A list that changes kind mid-run is two lists, not one confused one.
      if (list && list.ordered !== ordered) flushList();
      if (!list) list = { ordered, items: [] };
      list.items.push((bullet || numbered)[1]);
      return;
    }

    flushList();
    paragraph.push(trimmed);
  });

  flushAll();
  return blocks;
};

/**
 * The same text with its notation removed, for somewhere that cannot show
 * formatting — a card in a list, a meta description, a link title.
 *
 * Stripping rather than rendering: a card showing a teaser is a fixed few
 * lines, and an article's `## Section two` heading rendered at heading size
 * inside it would make one card twice the height of its neighbours. Leaving
 * the asterisks in is worse still.
 */
export const plainText = (raw) => String(raw || '')
  .replace(/^#{1,3}\s+/gm, '')
  .replace(/^>\s?/gm, '')
  .replace(/^[-*]\s+/gm, '')
  .replace(/^\d+[.)]\s+/gm, '')
  .replace(/\*\*([^*]+)\*\*/g, '$1')
  .replace(/\*([^*]+)\*/g, '$1')
  .replace(/`([^`]+)`/g, '$1')
  .replace(/\[([^\]]+)\]\([^)\s]+\)/g, '$1')
  .replace(/\n{2,}/g, '\n')
  .trim();

export default renderRichText;
